import { useMemo } from 'react';
import { Area, CartesianGrid, Legend, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { LoadingSpinner } from 'components';
import type { ActivityByHour } from 'services/api/AgentCenterAPI';

import styles from '../agentAnalytics.module.scss';

const CHART_HEIGHT = 280;

type ChartPoint = { hourLabel: string; calls: number };

const toCalls = (v: string | number | undefined): number => {
    if (typeof v === 'number' && !Number.isNaN(v)) return v;
    if (typeof v === 'string') {
        const n = Number(v);
        return Number.isNaN(n) ? 0 : n;
    }
    return 0;
};

const buildChartData = (activityByHour: ActivityByHour): ChartPoint[] => {
    const { buckets, timezone } = activityByHour;
    if (!buckets?.length) return [];

    const tz = timezone || undefined;
    const sorted = [...buckets].sort(
        (a, b) => new Date(String(a.start)).getTime() - new Date(String(b.start)).getTime()
    );

    return sorted.map((b) => {
        const start = b.start;
        let hourLabel: string;
        if (typeof start === 'string' || typeof start === 'number') {
            const d = new Date(start);
            hourLabel = Number.isNaN(d.getTime())
                ? String(start)
                : d.toLocaleTimeString('en-US', { timeZone: tz, hour: 'numeric', hour12: true });
        } else {
            hourLabel = '—';
        }
        return { hourLabel, calls: toCalls(b.calls) };
    });
};

const tooltipContentStyle = {
    backgroundColor: 'var(--bg-primary, #fff)',
    border: '1px solid var(--border-light, #eee)',
    borderRadius: '4px',
};

type AgentActivityChartProps = {
    activityByHour: ActivityByHour;
    isLoading?: boolean;
};

export const AgentActivityChart = ({ activityByHour, isLoading = false }: AgentActivityChartProps) => {
    const data = useMemo(() => buildChartData(activityByHour), [activityByHour]);

    return (
        <div className={styles.activityCard}>
            <div className={styles.activityHeader}>
                <h4 className='text-uppercase'>Activity</h4>
                <p className={styles.activitySubLabel}>Data updates every 30s</p>
            </div>
            {isLoading ? (
                <div className={styles.activityChartLoader}>
                    <LoadingSpinner size={28} text='' />
                </div>
            ) : data.length === 0 ? (
                <p className={styles.activityEmpty}>No call activity for this period</p>
            ) : (
                <div className={styles.activityChartWrap}>
                    <ResponsiveContainer width='100%' height={CHART_HEIGHT}>
                        <AreaChart data={data} margin={{ top: 28, right: 8, left: 0, bottom: 4 }}>
                            <CartesianGrid strokeDasharray='3 3' vertical={false} stroke='var(--border-light, #eee)' />
                            <XAxis
                                dataKey='hourLabel'
                                tickLine={false}
                                axisLine={false}
                                tick={{ fontSize: 10 }}
                                interval='preserveStartEnd'
                            />
                            <YAxis
                                tickLine={false}
                                axisLine={false}
                                tick={{ fontSize: 10 }}
                                allowDecimals={false}
                                width={32}
                                label={{ value: 'Calls', angle: -90, position: 'insideLeft', fontSize: 10 }}
                            />
                            <Tooltip
                                formatter={(value: number) => [value, 'Calls']}
                                labelFormatter={(label) => `Hour: ${label}`}
                                contentStyle={tooltipContentStyle}
                            />
                            <Legend
                                verticalAlign='top'
                                align='right'
                                iconType='line'
                                wrapperStyle={{ fontSize: 12, top: -14, color: 'var(--gray-1000, #555)' }}
                            />

                            <Area
                                type='monotone'
                                dataKey='calls'
                                name='Calls / hour'
                                stroke='var(--primary-purple, #9b51e0)'
                                fill='var(--primary-purple, #9b51e0)'
                                fillOpacity={0.3}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
};
